<template>
  <div v-if="shouldShowToolbar" class="fixed bottom-10 right-10 z-[999] no-print">
    <!-- Action Buttons (Roll Up) -->
    <Transition name="roll-up">
      <div v-if="showTools" class="flex flex-col items-center space-y-3 mb-4">
        <button
          v-for="(tool, index) in visibleTools"
          :key="index"
          @click="tool.action"
          :class="[
            'w-12 h-12 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center',
            tool.color || 'bg-blue-500 hover:bg-blue-600 text-white'
          ]"
          :title="tool.tooltip || tool.name"
        >
          <component :is="tool.icon" class="w-6 h-6" />
        </button>
      </div>
    </Transition>
    
    <!-- Main Toggle Button -->
    <button
      @click="toggleTools"
      :class="[
        'w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center',
        showTools 
          ? 'bg-gray-600 hover:bg-gray-700 text-white rotate-45' 
          : 'bg-main hover:bg-main-hover text-white'
      ]"
      :title="mainButtonTooltip"
    >
      <PlusIcon class="w-7 h-7" />
    </button>
  </div>
</template>

<script>
import { PlusIcon } from '@heroicons/vue/24/outline'

export default {
  name: 'FloatingAdminToolbar',
  components: {
    PlusIcon
  },
  props: {
    tools: {
      type: Array,
      required: true,
      validator: (tools) => {
        return tools.every(tool => 
          Object.prototype.hasOwnProperty.call(tool, 'name') && 
          Object.prototype.hasOwnProperty.call(tool, 'icon') && 
          Object.prototype.hasOwnProperty.call(tool, 'action') &&
          typeof tool.action === 'function'
        )
      }
    },
    mainButtonTooltip: {
      type: String,
      default: 'Admin Tools'
    }
  },
  data() {
    return {
      showTools: false
    }
  },
  computed: {
    visibleTools() {
      return this.tools.filter(tool => {
        // If no permission key is provided, only show for admin users
        if (!tool.permission) {
          return this.$userStore.isAdmin();
        }
        // Check if user has the specific permission
        return this.$userStore.hasPermission(tool.permission);
      });
    },
    shouldShowToolbar() {
      // Show toolbar if user has at least one visible tool
      return this.visibleTools.length > 0;
    }
  },
  methods: {
    toggleTools() {
      this.showTools = !this.showTools;
    }
  }
}
</script>

<style scoped>
/* Roll-up animation for admin tools */
.roll-up-enter-active,
.roll-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.roll-up-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.roll-up-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.roll-up-enter-to,
.roll-up-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* Staggered animation for multiple buttons */
.roll-up-enter-active button:nth-child(1) {
  transition-delay: 0.1s;
}

.roll-up-enter-active button:nth-child(2) {
  transition-delay: 0.2s;
}

.roll-up-enter-active button:nth-child(3) {
  transition-delay: 0.3s;
}

.roll-up-enter-active button:nth-child(4) {
  transition-delay: 0.4s;
}

.roll-up-enter-active button:nth-child(5) {
  transition-delay: 0.5s;
}
</style>
